import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Card,
  TextField,
  Button,
  Divider,
  Alert,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { ArrowBack, Add, Remove, DeleteOutline, TableRestaurant } from "@mui/icons-material";
import { apiClient } from "../utils/http";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { ORDER_TYPES } from "../utils/constants";
import { lineUnitPrice, lineTotal, formatPrice, buildOrderPayload } from "../utils/helpers";
import TableSelectSheet from "../components/TableSelectSheet";

export default function Cart() {
  const navigate = useNavigate();
  const { store } = useAuth();
  const {
    orderType,
    setOrderType,
    table,
    setTable,
    deliveryAddress,
    setDeliveryAddress,
    contactNumber,
    setContactNumber,
    cart,
    updateQuantity,
    removeItem,
    clearCart,
    orderNote,
    setOrderNote,
    total,
  } = useCart();
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState("");
  const [tableAreas, setTableAreas] = useState([]);
  const [tableSheetOpen, setTableSheetOpen] = useState(false);

  const currency = store?.currency || store?.price_type || "";

  // Store-level restriction (set from denzo-pos's POS Settings, persisted to
  // the same store.settings the pos/v1/init payload already carries here) —
  // only offer the order types the store actually wants waiters using. No
  // restriction configured (key missing, or every value false) means every
  // type is still offered, so this stays backward compatible for stores that
  // haven't visited that setting yet.
  const waiterOrderTypes = store?.settings?.waiter_order_types;
  const hasWaiterTypeRestriction =
    waiterOrderTypes && Object.values(waiterOrderTypes).some(Boolean);
  const visibleOrderTypes = hasWaiterTypeRestriction
    ? ORDER_TYPES.filter((t) => waiterOrderTypes[t.value])
    : ORDER_TYPES;

  useEffect(() => {
    if (orderType !== "dine_in") return;
    // GET pos/v1/table-areas — POSController@getTableAreas — fetched here (not
    // just relying on what Home already loaded) so Cart works as the
    // authoritative place to pick/confirm the table on its own.
    apiClient
      .get("table-areas")
      .then((res) => setTableAreas(res.data?.table_areas || []))
      .catch(() => {});
  }, [orderType]);

  const handlePlaceOrder = async () => {
    if (orderType === "dine_in" && !table) {
      setError("Select a table before placing the order.");
      return;
    }
    if (orderType === "delivery" && !deliveryAddress.trim()) {
      setError("Enter a delivery address before placing the order.");
      return;
    }
    if (orderType === "delivery" && !contactNumber.trim()) {
      setError("Enter a contact number before placing the order.");
      return;
    }
    setPlacing(true);
    setError("");
    try {
      // POST pos/v1/orders/create — OrderController@createOrder — starts a draft
      // order (status "created"); response is Order::...->find($order->id) under
      // the `order` key (OrderController.php:161-164), with an `.id` fallback
      // kept here just in case that shape ever changes.
      const createRes = await apiClient.post("orders/create");
      const draftId = createRes.data?.order?.id ?? createRes.data?.id;

      // A real order's delivery_address is stored as {address: "..."} (confirmed
      // from live data), not a plain string, so it's wrapped here — the address
      // field itself just holds free text, this app doesn't break it into
      // street/city/etc.
      const payload = buildOrderPayload({
        cart,
        orderType,
        table,
        deliveryAddress: orderType === "delivery" ? { address: deliveryAddress } : undefined,
        contactNumber,
        orderNote,
        existingOrderId: draftId,
      });

      // POST pos/v1/orders/place — OrderController@placeOrder — reads the order
      // as a JSON-encoded string under the `order` field (json_decode($request->order)).
      // kot_print asks the backend to dispatch the KOT print in-process, in this
      // same request (OrderController.php:434) — same param LiveOrders.js already
      // sends on every save; a brand-new order is entirely new kitchen work, so
      // this is unconditional here (LiveOrders only sends it when hasNewKitchenWork).
      const placeRes = await apiClient.post("orders/place", {
        order: JSON.stringify(payload),
        kot_print: 1,
      });
      const placedOrder = placeRes.data?.order || {};

      clearCart();
      navigate("/order-confirmation", {
        state: {
          order: placedOrder,
          table: orderType === "dine_in" ? table : null,
          // Order is already saved at this point — a print failure shouldn't
          // block navigation, just be surfaced on the confirmation screen so
          // the waiter knows to flag it rather than assume the kitchen got it.
          kotPrintError: placeRes.data?.kot_print_error || null,
        },
      });
    } catch (err) {
      setError(err.message || "Failed to place order");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <Box sx={{ pb: 14 }}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider" }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            Your Order
          </Typography>
          {orderType === "dine_in" && table && <Chip label={table.name} color="primary" size="small" />}
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <ToggleButtonGroup
          value={orderType}
          exclusive
          fullWidth
          size="small"
          onChange={(_, v) => v && setOrderType(v)}
          sx={{ mb: 1.5 }}
        >
          {visibleOrderTypes.map((t) => (
            <ToggleButton key={t.value} value={t.value} sx={{ textTransform: "none", fontWeight: 600 }}>
              {t.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {orderType === "dine_in" && (
          <Button
            variant="outlined"
            fullWidth
            startIcon={<TableRestaurant />}
            onClick={() => setTableSheetOpen(true)}
            sx={{ mb: 2, justifyContent: "flex-start" }}
          >
            {table ? `${table.areaName} · ${table.name}` : "Select a table"}
          </Button>
        )}

        {orderType === "delivery" && (
          <>
            <TextField
              label="Contact number"
              type="tel"
              fullWidth
              size="small"
              value={contactNumber}
              onChange={(e) => setContactNumber(e.target.value)}
              sx={{ mb: 1.5 }}
            />
            <TextField
              label="Delivery address"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              sx={{ mb: 2 }}
            />
          </>
        )}

        {cart.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
            Your cart is empty.
          </Typography>
        ) : (
          cart.map((item) => (
            <Card key={item.cartId} variant="outlined" sx={{ p: 1.5, mb: 1.25, borderRadius: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {item.product.name}
                  </Typography>
                  {item.option && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.option.name}
                    </Typography>
                  )}
                  {item.addons.length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      + {item.addons.map((a) => a.name).join(", ")}
                    </Typography>
                  )}
                  {item.notes && (
                    <Typography variant="caption" color="text.disabled" display="block">
                      Note: {item.notes}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={() => removeItem(item.cartId)}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                  <IconButton size="small" onClick={() => updateQuantity(item.cartId, item.quantity - 1)}>
                    <Remove fontSize="small" />
                  </IconButton>
                  <Typography sx={{ px: 1.5, minWidth: 20, textAlign: "center" }}>{item.quantity}</Typography>
                  <IconButton size="small" onClick={() => updateQuantity(item.cartId, item.quantity + 1)}>
                    <Add fontSize="small" />
                  </IconButton>
                </Box>
                <Typography fontWeight={700}>{formatPrice(lineTotal(item), currency)}</Typography>
              </Box>
              {item.quantity > 1 && (
                <Typography variant="caption" color="text.secondary">
                  {formatPrice(lineUnitPrice(item), currency)} each
                </Typography>
              )}
            </Card>
          ))
        )}

        {cart.length > 0 && (
          <TextField
            label="Order note (optional)"
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={orderNote}
            onChange={(e) => setOrderNote(e.target.value)}
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      {cart.length > 0 && (
        <Box sx={{ position: "fixed", left: 0, right: 0, bottom: 0, bgcolor: "#fff", borderTop: "1px solid", borderColor: "divider", p: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
            <Typography variant="h6">Total</Typography>
            <Typography variant="h6" fontWeight={800}>
              {formatPrice(total, currency)}
            </Typography>
          </Box>
          <Button fullWidth variant="contained" size="large" disabled={placing} onClick={handlePlaceOrder} sx={{ py: 1.4 }}>
            {placing ? "Placing order..." : "Place Order"}
          </Button>
        </Box>
      )}

      <TableSelectSheet
        open={tableSheetOpen}
        onClose={() => setTableSheetOpen(false)}
        tableAreas={tableAreas}
        selectedTableId={table?.id}
        onSelect={(t) => {
          setTable(t);
          setTableSheetOpen(false);
        }}
      />
    </Box>
  );
}
